import { client } from '@/shared/api/client'
import { assertValidVrmBlob } from '../lib/vrmBlobValidation'
import { readAvatarApiErrorBody, readRequiredHeader } from './response'

export const DEFAULT_AVATAR_NAME = 'avatar_sample_a.vrm'

export const buildDefaultAvatarApiPath = (avatarName: string = DEFAULT_AVATAR_NAME): string =>
  `/api/avatar/default/${avatarName}`

export type GetDefaultAvatarParams = {
  avatarName?: string
  ifNoneMatch?: string
}

export type DefaultAvatarFetchResult =
  | {
      status: 'ok'
      avatarName: string
      avatarVersion: string
      vrmBlob: Blob
    }
  | {
      status: 'not_modified'
      avatarName: string
      avatarVersion: string
    }

export const getDefaultAvatar = async ({
  avatarName = DEFAULT_AVATAR_NAME,
  ifNoneMatch,
}: GetDefaultAvatarParams = {}): Promise<DefaultAvatarFetchResult> => {
  const response = await client.api.avatar.default[':avatar_name'].$get(
    {
      param: { avatar_name: avatarName },
    },
    {
      init: {
        cache: 'no-store',
        headers: ifNoneMatch ? { 'If-None-Match': ifNoneMatch } : undefined,
      },
    }
  )

  if (response.status === 304) {
    return {
      status: 'not_modified',
      avatarName,
      avatarVersion: readRequiredHeader(response, 'ETag'),
    }
  }

  if (!response.ok) {
    const body = await readAvatarApiErrorBody(response)
    throw new Error(body?.error ?? body?.message ?? `failed to fetch default avatar: ${response.status}`)
  }

  const vrmBlob = await response.blob()
  await assertValidVrmBlob(vrmBlob, `default avatar API (${avatarName})`)

  return {
    status: 'ok',
    avatarName,
    avatarVersion: readRequiredHeader(response, 'ETag'),
    vrmBlob,
  }
}
